import styled from '@emotion/styled';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';
import { GAME_MODE, PlayerSetup, SingSetup, SongPreview } from '~/interfaces';
import events from '~/modules/GameEvents/GameEvents';
import useViewportSize from '~/modules/hooks/useViewportSize';
import GameSettings from '~/routes/SingASong/SongSelection/Components/SongSettings/GameSettings';
import MicCheck from '~/routes/SingASong/SongSelection/Components/SongSettings/MicCheck';
import PlayerSettings from '~/routes/SingASong/SongSelection/Components/SongSettings/PlayerSettings';

interface Props {
  songPreview: SongPreview;
  onPlay: (setup: SingSetup & { song: SongPreview }) => void;
  keyboardControl: boolean;
  onExitKeyboardControl: () => void;
  context?: 'desktopPreview' | 'mobilePreview' | 'page';
}

export default function SongSettings({
  songPreview,
  onPlay,
  keyboardControl,
  onExitKeyboardControl,
  context = 'page',
}: Props) {
  const [singSetup, setSingSetup] = useState<SingSetup | null>(null);
  const [step, setStep] = useState<'song' | 'players'>('song');

  const { width } = useViewportSize();
  const isMobile = (width ?? 9999) < 640;

  const isPreview = context === 'desktopPreview' || context === 'mobilePreview';
  const micVariant: 'default' | 'mobilePreview' | 'desktopPreview' =
    context === 'desktopPreview' ? 'desktopPreview' : context === 'mobilePreview' ? 'mobilePreview' : 'default';

  const onSongStepFinish = (setup: SingSetup) => {
    setSingSetup(setup);
    setStep('players');
  };

  const startSong = (players: PlayerSetup[]) => {
    if (!singSetup) return;
    const finalSetup = { ...singSetup, players };
    events.songStarted.dispatch(songPreview, finalSetup);
    onPlay({ song: songPreview, ...finalSetup });
  };

  return (
    <Scaffold data-preview={isPreview} data-mobile={isMobile}>
      {isPreview && (
        <TopBar>
          <MicCheck variant={micVariant} />
        </TopBar>
      )}

      <Body data-preview={isPreview}>
        <AnimatePresence mode="wait">
          <Content
            key={step}
            transition={{ duration: 0.2 }}
            initial={{ opacity: 0, translateX: isMobile ? 0 : 120, translateY: isMobile ? 10 : 0 }}
            animate={{ opacity: 1, translateX: 0, translateY: 0 }}
            exit={{ opacity: 0, translateX: isMobile ? 0 : 120, translateY: isMobile ? 10 : 0 }}>
            {step === 'song' && (
              <GameSettings
                songPreview={songPreview}
                onNextStep={onSongStepFinish}
                keyboardControl={keyboardControl}
                onExitKeyboardControl={onExitKeyboardControl}
                context={isPreview ? 'preview' : 'page'}
              />
            )}

            {step === 'players' && (
              <PlayerSettings
                songPreview={songPreview}
                onNextStep={startSong}
                keyboardControl={keyboardControl}
                onExitKeyboardControl={() => setStep('song')}
                gameMode={singSetup?.mode ?? GAME_MODE.DUEL}
                context={isPreview ? 'mobilePreview' : 'default'}
              />
            )}
          </Content>
        </AnimatePresence>
      </Body>
    </Scaffold>
  );
}

const Scaffold = styled.div`
  width: 100%;

  &[data-preview='true'] {
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
  }
`;

const TopBar = styled.div`
  position: sticky;
  top: 0;
  z-index: 6;
  padding: 10px 0 14px;
  background: var(--walky-surface-translucent);
  border-bottom: 1px solid var(--walky-border);
`;

const Body = styled.div`
  flex: 1;
  min-height: 0;

  &[data-preview='true'] {
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: calc(env(safe-area-inset-bottom) + 16px);
  }
`;

const Content = styled(motion.div)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;
